import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import { Calendar, Clock, Users, User, Mail, Phone, Utensils, CheckCircle } from 'lucide-react';
import { getBackendUrl } from '../utils/config';

const API_URL = getBackendUrl();

const PublicReservationPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    date: '',
    time: '19:00',
    guest_name: '',
    guest_count: '2',
    phone: '',
    email: '',
    reservation_type: 'diner',
    notes: ''
  });

  const reservationTypes = [
    { value: 'ontbijt', label: 'Ontbijt', icon: '🌅' },
    { value: 'lunch', label: 'Lunch', icon: '🥗' },
    { value: 'diner', label: 'Diner', icon: '🍽️' },
    { value: 'borrel', label: 'Borrel', icon: '🍸' },
    { value: 'feest', label: 'Feest', icon: '🎉' },
    { value: 'vergadering', label: 'Vergadering', icon: '💼' },
    { value: 'anders', label: 'Anders', icon: '📝' }
  ];

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
    '20:00', '20:30', '21:00', '21:30', '22:00'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/public/reservations`, {
        ...form,
        guest_count: parseInt(form.guest_count) || 2
      });
      setSubmitted(true);
      toast.success('Reservering ontvangen!');
    } catch (error) {
      toast.error('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FEF3E2] to-[#FDFBF7] flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1C1917] mb-3">Bedankt voor uw reservering!</h2>
            <p className="text-[#57534E] mb-6">
              Wij hebben uw aanvraag ontvangen. U ontvangt binnen 24 uur een bevestiging per email.
            </p>
            <div className="bg-stone-50 rounded-lg p-4 text-left space-y-2">
              <div className="flex justify-between">
                <span className="text-[#78716C]">Datum:</span>
                <span className="font-medium">{new Date(form.date).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">Tijd:</span>
                <span className="font-medium">{form.time} uur</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">Personen:</span>
                <span className="font-medium">{form.guest_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">Type:</span>
                <span className="font-medium capitalize">{form.reservation_type}</span>
              </div>
            </div>
            <Button 
              className="mt-6 w-full bg-[#C2410C] hover:bg-[#9A3412]"
              onClick={() => {
                setSubmitted(false);
                setForm({
                  date: '',
                  time: '19:00',
                  guest_name: '',
                  guest_count: '2',
                  phone: '',
                  email: '',
                  reservation_type: 'diner',
                  notes: ''
                });
              }}
            >
              Nieuwe reservering
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FEF3E2] to-[#FDFBF7] py-8 px-4">
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#C2410C] to-[#D97706] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Utensils className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1C1917] mb-2">Reserveren</h1>
          <p className="text-[#57534E]">Maak online een reservering en wij bevestigen deze zo snel mogelijk</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="border-b bg-stone-50/50">
            <CardTitle className="text-lg">Reserveringsgegevens</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Reservation Type */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Type reservering</Label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {reservationTypes.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setForm({ ...form, reservation_type: type.value })}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        form.reservation_type === type.value 
                          ? 'border-[#C2410C] bg-orange-50' 
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <span className="text-xl block mb-1">{type.icon}</span>
                      <span className="text-xs font-medium text-[#1C1917]">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#C2410C]" />
                    Datum
                  </Label>
                  <Input
                    type="date"
                    min={today}
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#C2410C]" />
                    Tijd
                  </Label>
                  <Select value={form.time} onValueChange={(value) => setForm({ ...form, time: value })}>
                    <SelectTrigger className="h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Guest Count */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#C2410C]" />
                  Aantal personen
                </Label>
                <Select value={form.guest_count} onValueChange={(value) => setForm({ ...form, guest_count: value })}>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12,15,20,25,30].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'persoon' : 'personen'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contact Details */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-[#1C1917]">Contactgegevens</h3>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#C2410C]" />
                    Naam
                  </Label>
                  <Input
                    value={form.guest_name}
                    onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                    placeholder="Uw volledige naam"
                    required
                    className="h-12"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#C2410C]" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="email@voorbeeld.nl"
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#C2410C]" />
                      Telefoon
                    </Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="06-12345678"
                      required
                      className="h-12"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Opmerkingen of speciale wensen</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Bijv. allergieën, kinderstoel, rolstoel toegankelijk, etc."
                  rows={3}
                />
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                className="w-full h-14 text-lg bg-[#C2410C] hover:bg-[#9A3412]"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verzenden...
                  </div>
                ) : (
                  'Reservering aanvragen'
                )}
              </Button>

              <p className="text-xs text-center text-[#78716C]">
                Na het verzenden ontvangt u een bevestiging per email zodra wij uw reservering hebben goedgekeurd.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicReservationPage;
